import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  useTheme,
  useMediaQuery,
  Container,
  Fade,
  Grow,
  Zoom,
  Slide,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  InsertDriveFile,
  Description,
  AccountBalance,
  PhoneAndroid,
  Info,
  Close,
  Check,
  FileUpload,
  UploadFile,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { uploadFile, uploadBankStatement } from '../api';
import toast from 'react-hot-toast';

const Upload = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [file, setFile] = useState(null);
  const [statementType, setStatementType] = useState('mpesa');
  const [uploadProgress, setUploadProgress] = useState(0);

  const onDrop = (acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadResult(null);
      setUploadProgress(0);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxSize: 10485760, // 10MB
    multiple: false
  });

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadProgress(10);

    try {
      let response;

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) return prev + 10;
          return prev;
        });
      }, 300);

      // Choose the appropriate API endpoint based on statement type
      if (statementType === 'mpesa') {
        response = await uploadFile(file);
      } else if (statementType === 'bank') {
        response = await uploadBankStatement(file);
      }

      clearInterval(progressInterval);
      setUploadProgress(100);

      setUploadResult({
        success: true,
        message: response.message,
        data: response.data
      });

      setTimeout(() => {
        toast.success(`${statementType.charAt(0).toUpperCase() + statementType.slice(1)} statement uploaded successfully!`);
      }, 300);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadResult({
        success: false,
        message: error.message || 'Upload failed',
        details: error.details || null
      });
      toast.error(error.message || 'Upload failed');
    } finally {
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 500);
    }
  };

  const handleReset = () => {
    setFile(null);
    setUploadResult(null);
    setUploadProgress(0);
  };

  const getStatementTypeIcon = () => {
    if (statementType === 'mpesa') {
      return <PhoneAndroid sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.primary.main }} />;
    } else {
      return <AccountBalance sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.secondary.main }} />;
    }
  };

  const getStatementTypeDescription = () => {
    if (statementType === 'mpesa') {
      return 'Upload M-Pesa statements with columns: Receipt No., Completion Time, Details, Paid In, Withdrawn, Balance';
    } else {
      return 'Upload Bank statements with columns: Posting Date, Value Date, Bank Reference, Transaction Type, Debit Amount, Credit Amount, Running Balance';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 },
        gap: { xs: 1, sm: 0 }
      }}>
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom sx={{ fontWeight: 'bold' }}>
            Upload Transactions
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Upload CSV or Excel files with M-Pesa or Bank transactions
          </Typography>
        </Box>
        {file && (
          <Chip
            icon={<InsertDriveFile />}
            label={file.name}
            onDelete={handleReset}
            color="primary"
            variant="outlined"
            sx={{ maxWidth: { xs: '100%', sm: 300 }, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
          />
        )}
      </Box>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Left Column - Upload Area */}
        <Grid item xs={12} lg={8}>
          {/* Statement Type Selector */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Select Statement Type
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                  <Paper
                    onClick={() => !uploading && setStatementType('mpesa')}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      border: '2px solid',
                      borderColor: statementType === 'mpesa' ? 'primary.main' : 'grey.300',
                      backgroundColor: statementType === 'mpesa' ? 'primary.light' : 'transparent',
                      transition: 'all 0.3s ease',
                      opacity: uploading ? 0.6 : 1,
                      '&:hover': {
                        borderColor: uploading ? 'grey.300' : 'primary.main',
                        backgroundColor: uploading ? 'transparent' : 'action.hover',
                        transform: uploading ? 'none' : 'translateY(-2px)',
                        boxShadow: uploading ? 'none' : theme.shadows[4],
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                      <PhoneAndroid sx={{
                        fontSize: { xs: 32, sm: 40 },
                        color: statementType === 'mpesa' ? 'primary.main' : 'grey.400'
                      }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" fontSize={{ xs: '0.9rem', sm: '1rem' }}>
                          M-Pesa Statement
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display={{ xs: 'none', sm: 'block' }}>
                          Mobile money transactions
                        </Typography>
                      </Box>
                      {statementType === 'mpesa' && (
                        <Fade in={true}>
                          <CheckCircle sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 24 } }} />
                        </Fade>
                      )}
                    </Box>
                  </Paper>
                </Zoom>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                  <Paper
                    onClick={() => !uploading && setStatementType('bank')}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      border: '2px solid',
                      borderColor: statementType === 'bank' ? 'secondary.main' : 'grey.300',
                      backgroundColor: statementType === 'bank' ? 'secondary.light' : 'transparent',
                      transition: 'all 0.3s ease',
                      opacity: uploading ? 0.6 : 1,
                      '&:hover': {
                        borderColor: uploading ? 'grey.300' : 'secondary.main',
                        backgroundColor: uploading ? 'transparent' : 'action.hover',
                        transform: uploading ? 'none' : 'translateY(-2px)',
                        boxShadow: uploading ? 'none' : theme.shadows[4],
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                      <AccountBalance sx={{
                        fontSize: { xs: 32, sm: 40 },
                        color: statementType === 'bank' ? 'secondary.main' : 'grey.400'
                      }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" fontSize={{ xs: '0.9rem', sm: '1rem' }}>
                          Bank Statement
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display={{ xs: 'none', sm: 'block' }}>
                          Bank account transactions
                        </Typography>
                      </Box>
                      {statementType === 'bank' && (
                        <Fade in={true}>
                          <CheckCircle sx={{ color: 'secondary.main', fontSize: { xs: 20, sm: 24 } }} />
                        </Fade>
                      )}
                    </Box>
                  </Paper>
                </Zoom>
              </Grid>
            </Grid>
            <Fade in={true}>
              <Box sx={{
                mt: 2,
                p: { xs: 1.5, sm: 2 },
                bgcolor: 'info.light',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                <Info sx={{ fontSize: 20, color: 'info.main', mt: 0.5, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" fontSize={{ xs: '0.7rem', sm: '0.75rem' }}>
                  {getStatementTypeDescription()}
                </Typography>
              </Box>
            </Fade>
          </Paper>

          {/* Dropzone */}
          <Grow in={true}>
            <Paper
              {...getRootProps()}
              sx={{
                p: { xs: 3, sm: 4 },
                textAlign: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'grey.300',
                backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                minHeight: { xs: '150px', sm: '200px', md: '250px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                opacity: uploading ? 0.6 : 1,
                '&:hover': {
                  borderColor: uploading ? 'grey.300' : 'primary.main',
                  backgroundColor: uploading ? 'transparent' : 'action.hover',
                },
              }}
            >
              <input {...getInputProps()} disabled={uploading} />
              {uploading ? (
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body1" gutterBottom>
                    Uploading...
                  </Typography>
                  <Box sx={{ width: '100%', maxWidth: 300, mx: 'auto', mt: 1 }}>
                    <Box sx={{
                      width: '100%',
                      height: 4,
                      bgcolor: 'grey.200',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}>
                      <Box sx={{
                        width: `${uploadProgress}%`,
                        height: '100%',
                        bgcolor: 'primary.main',
                        transition: 'width 0.3s ease',
                      }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {uploadProgress}%
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  {getStatementTypeIcon()}
                  <CloudUpload sx={{ fontSize: { xs: 48, sm: 64 }, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom fontSize={{ xs: '1rem', sm: '1.25rem' }}>
                    {isDragActive ? 'Drop your file here' : 'Drag & drop your file here'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                    Supported formats: CSV, XLSX, XLS (Max 10MB)
                  </Typography>
                </>
              )}
            </Paper>
          </Grow>
        </Grid>

        {/* Right Column - File Information */}
        <Grid item xs={12} lg={4}>
          <Slide direction="left" in={true}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom fontSize={{ xs: '1rem', sm: '1.25rem' }}>
                  File Information
                </Typography>

                {file ? (
                  <Fade in={true}>
                    <Box>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                        p: 1.5,
                        bgcolor: 'primary.light',
                        borderRadius: 1,
                      }}>
                        <InsertDriveFile sx={{ color: 'primary.main' }} />
                        <Typography variant="body2" fontWeight="bold" noWrap sx={{ flex: 1 }}>
                          {file.name}
                        </Typography>
                        <IconButton size="small" onClick={handleReset} disabled={uploading}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Size
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatFileSize(file.size)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Type
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {file.type ? file.type.split('/')[1]?.toUpperCase() || 'Unknown' : 'Unknown'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Statement Type
                          </Typography>
                          <Chip
                            size="small"
                            label={statementType.charAt(0).toUpperCase() + statementType.slice(1)}
                            color={statementType === 'mpesa' ? 'primary' : 'secondary'}
                            sx={{ mt: 0.5, fontWeight: 'bold' }}
                            icon={statementType === 'mpesa' ? <PhoneAndroid /> : <AccountBalance />}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Fade>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: { xs: 3, sm: 4 },
                  }}>
                    <InsertDriveFile sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                    <Typography color="text.secondary" align="center">
                      No file selected
                    </Typography>
                    <Typography variant="caption" color="text.secondary" align="center">
                      Select a file to begin
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Upload Button */}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  size="large"
                  startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadFile />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textTransform: 'none',
                    bgcolor: !file || uploading ? 'grey.400' : 'primary.main',
                    '&:hover': {
                      bgcolor: !file || uploading ? 'grey.400' : 'primary.dark',
                    },
                  }}
                >
                  {uploading ? `Uploading ${uploadProgress}%` : 'Upload File'}
                </Button>

                {/* Upload Result */}
                <Collapse in={!!uploadResult}>
                  <Box sx={{ mt: 3 }}>
                    <Alert
                      severity={uploadResult?.success ? 'success' : 'error'}
                      onClose={handleReset}
                      icon={uploadResult?.success ? <CheckCircle /> : <ErrorIcon />}
                      sx={{ borderRadius: 2 }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {uploadResult?.message}
                      </Typography>
                      {uploadResult?.success && uploadResult?.data && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Source: {uploadResult.data.source_type?.toUpperCase() || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Processed: {uploadResult.data.processed || uploadResult.data.rowsProcessed || 0} transactions
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Inserted: {uploadResult.data.insertedRows || uploadResult.data.total || 0} rows
                          </Typography>
                          {uploadResult.data.sample && uploadResult.data.sample.length > 0 && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Sample: {JSON.stringify(uploadResult.data.sample[0]).substring(0, 100)}...
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      {uploadResult?.details && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {uploadResult.details}
                        </Typography>
                      )}
                    </Alert>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>

      {/* Supported Formats Info */}
      <Fade in={true} style={{ transitionDelay: '300ms' }}>
        <Paper sx={{ p: { xs: 2, sm: 3 }, mt: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Supported Formats
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 1.5, bgcolor: 'primary.light', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" component="div">
                  <strong style={{ color: theme.palette.primary.main }}>M-Pesa Statement</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                    <li>Receipt No.</li>
                    <li>Completion Time</li>
                    <li>Details</li>
                    <li>Paid In</li>
                    <li>Withdrawn</li>
                    <li>Balance</li>
                  </ul>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 1.5, bgcolor: 'secondary.light', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" component="div">
                  <strong style={{ color: theme.palette.secondary.main }}>Bank Statement</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                    <li>Posting Date</li>
                    <li>Value Date</li>
                    <li>Bank Reference</li>
                    <li>Transaction Type</li>
                    <li>Debit Amount</li>
                    <li>Credit Amount</li>
                    <li>Running Balance</li>
                  </ul>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Fade>
    </Container>
  );
};

export default Upload;